import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import { parse, stringify } from "query-string";
import React from "react";

import TextSearch from "@/mk2/apps/bazaar/components/TextSearch";
import BlogFeedPostsWithPager from "@/mk2/apps/blogs/components/BlogFeedPostsWithPager";
import SearchResults from "@/mk2/apps/blogs/components/SearchResults";
import { BlogsFeedCategory, Props } from "@/mk2/apps/blogs/containers/BlogsFeed/BlogsFeedPage";
import { blogsUrl } from "@/mk2/apps/blogs/urls";
import { ScrollToPosition } from "@/mk2/apps/groups/containers/GroupSubscribedFeed/GroupSubscribedFeedPage";
import { homepageUrl } from "@/mk2/apps/home/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Failure } from "@/mk2/components/errors/Failure";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { url } from "@/mk2/helpers/urls";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import {
    BACK_TO_HOMEPAGE,
    BLOGS_SEARCH_PLACEHOLDER,
    HOMEPAGE_PR_ARTICLES_TITLE,
    NO_SEARCH_RESULTS_FOR,
    PB_MENU_MY_ACTIVITY,
    SEARCH_RESULTS_FOR,
} from "@/mk/autogenerated/translation";

import styles from "./BlogsFeed.mscss";

interface State {
    query: string;
}

export function getCategoryTitle(category: BlogsFeedCategory) {
    return {
        [BlogsFeedCategory.FLAGGED]: "Flagged",
        [BlogsFeedCategory.FLAGGED_GROUPS]: "Flagged groups",
        [BlogsFeedCategory.MY_ACTIVITY]: PB_MENU_MY_ACTIVITY,
        [BlogsFeedCategory.PR_ARTICLES]: HOMEPAGE_PR_ARTICLES_TITLE,
    }[category];
}

class BlogsFeed extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            query: props.query,
        };
    }

    public componentDidMount() {
        const { loadingState, onScrollTo } = this.props;

        if (loadingState === LoadingState.SUCCESS_ON_SERVER) {
            const qs = parse(this.props.location.search);
            if (qs.scrollto) {
                onScrollTo(qs.scrollto as ScrollToPosition);
            }
        }
    }

    public componentDidUpdate(prevProps: Props) {
        const p = this.props;

        if (p.query !== prevProps.query) {
            this.setState({ query: p.query });
        }

        if (
            // zmenil sa feed (url) alebo page z feedu
            (p.location.pathname !== prevProps.location.pathname ||
                p.pageRef !== prevProps.pageRef) &&
            p.loadedAt < Date.now() - 30 * 1000 &&
            isSuccess(p.loadingState)
        ) {
            this.load();
        }

        if (
            // zmenil sa feed (url) alebo page z feedu
            (p.location.pathname !== prevProps.location.pathname ||
                p.pageRef !== prevProps.pageRef) &&
            // nevraciame sa cez 'Back'
            p.history.action !== "POP" &&
            // a nacitali sme z cache (LoadingSwitch nespustil blogsFeedFetchTrigger)
            isSuccess(p.loadingState)
        ) {
            // defaultne sa scrollne na konci fetchGroupSubscribedFeed sagy, ale ta sa nespusti
            // tak vykonaj scroll side-effect
            const qs = parse(p.location.search);
            if (qs.scrollto) {
                p.onScrollTo(qs.scrollto as ScrollToPosition);
            }
        }
    }

    public render() {
        const { category, loadingState, anyPageLoaded } = this.props;

        const categoryTitle = getCategoryTitle(category);

        return (
            <div className={styles.BlogsFeed}>
                {categoryTitle && <h1 className={styles.BlogsFeed__header}>{categoryTitle}</h1>}
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderInit={this.renderInitOrSuccess}
                    onRenderSuccess={this.renderInitOrSuccess}
                    onRenderFailure={this.renderFailure}
                    hasData={anyPageLoaded}
                />
            </div>
        );
    }

    private load = () => {
        const { category, location, onLoad, pageRef, posts, postType, query } = this.props;
        const numPostsBeforeLoad = posts.length;
        onLoad(pageRef, category, postType, query, location, numPostsBeforeLoad);
    };

    private renderFailure = () => {
        const { staticContext, errorMessage } = this.props;

        return (
            <Failure
                errorMessage={errorMessage}
                staticContext={staticContext}
                backButton={
                    <Btn
                        type={BtnType.Blue}
                        label={BACK_TO_HOMEPAGE}
                        icon={faArrowLeft}
                        link={homepageUrl}
                    />
                }
                onRetry={this.load}
            />
        );
    };

    private renderInitOrSuccess = () => {
        const {
            brandDaysPostId,
            category,
            commentsCounts,
            complaintsCounts,
            complaintsOpened,
            errorMessage,
            hashtags,
            isMobile,
            loadingState,
            location,
            match,
            onDelete,
            onIgnoreUser,
            onPostToggleMenuMore,
            onPostToggleVisibilityMenuMore,
            onReport,
            onSetDisplayingAtFeed,
            onSetVisibility,
            pageRef,
            posts,
            postsSubtreeComplaintList,
            query,
            requestUser,
            requestUserPermissions,
            staticContext,
            userAgent,
        } = this.props;

        return (
            <React.Fragment>
                {category === BlogsFeedCategory.ALL && (
                    <>
                        <div className={styles.BlogsFeed__search__container}>
                            <TextSearch
                                value={this.state.query || ""}
                                onChange={this.onSearchChange}
                                placeholder={BLOGS_SEARCH_PLACEHOLDER}
                                hideButtonUntilFocused
                            />
                        </div>

                        {query && (
                            <SearchResults
                                loadingState={loadingState}
                                hasData={Boolean(posts && posts.length > 0)}
                                onCancelFilters={this.onCancelFilters}
                            >
                                <h2 className={styles.BlogsFeed__search__headline}>
                                    {posts && posts.length > 0
                                        ? SEARCH_RESULTS_FOR
                                        : NO_SEARCH_RESULTS_FOR}{" "}
                                    <span>&ldquo;{query}&rdquo;</span>
                                </h2>
                            </SearchResults>
                        )}
                    </>
                )}
                <div id="scrolltoFirstHere" />{" "}
                {/* musi byt vzdy vyrenderovane v DOMe, pouziva sa zo sagy */}
                <BlogFeedPostsWithPager
                    brandDaysPostId={brandDaysPostId}
                    category={category}
                    commentsCounts={commentsCounts}
                    complaintsCounts={complaintsCounts}
                    complaintsOpened={complaintsOpened}
                    errorMessage={errorMessage}
                    hashtags={category === BlogsFeedCategory.ALL && hashtags}
                    history={this.props.history}
                    isMobile={isMobile}
                    loadingState={loadingState}
                    location={location}
                    match={match}
                    numberOfPages={undefined} // countless pager
                    onDelete={onDelete}
                    onIgnoreUser={onIgnoreUser}
                    onPostToggleMenuMore={onPostToggleMenuMore}
                    onPostToggleVisibilityMenuMore={onPostToggleVisibilityMenuMore}
                    onReport={onReport}
                    onSetDisplayingAtFeed={onSetDisplayingAtFeed}
                    onSetVisibility={onSetVisibility}
                    pageRef={pageRef}
                    permissions={requestUserPermissions}
                    posts={posts}
                    postsSubtreeComplaintList={postsSubtreeComplaintList}
                    requestUser={requestUser}
                    showHashtagsBox={category === BlogsFeedCategory.ALL}
                    showStrips
                    staticContext={staticContext}
                    userAgent={userAgent}
                />
                <div id="scrolltoLastHere" />{" "}
                {/* musi byt vzdy vyrenderovane v DOMe, pouziva sa zo sagy */}
            </React.Fragment>
        );
    };

    private commonRedirect = (query) => {
        const {
            match: { params },
        } = this.props;

        const search = {
            ...(query !== null ? { query } : {}),
        };

        this.props.history.replace({
            pathname: url(blogsUrl, params),
            search: search ? stringify(search) : undefined,
        });
    };

    private onSearchChange = (searchStr: string) => {
        searchStr = searchStr || null;
        this.setState({ query: searchStr });
        this.commonRedirect(searchStr);
    };

    private onCancelFilters = () => {
        this.onSearchChange(null);
    };
}

export default BlogsFeed;
